import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  CreditCard as CreditCardIcon,
  Shield as ShieldIcon,
  Message as MessageIcon,
  HourglassEmpty as HourglassIcon,
  Search as SearchIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

const StatCard = ({ icon, label, value, color = 'primary', onClick, subtitle }) => (
  <Card
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 3 } : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            p: 1.5,
            borderRadius: 2,
            display: 'flex',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {value ?? '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="primary.main">
              Click to view →
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStats();
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load stats:', err);
      setError('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/users?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Overview of Second Clear system
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Quick Search */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Search
        </Typography>
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            fullWidth
            placeholder="Search by email, phone, name, or Stripe ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton type="submit" edge="end">
                    <ArrowIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Stats Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<PeopleIcon />}
            label="Total Users"
            value={stats?.total_users}
            color="primary"
            onClick={() => navigate('/users')}
            subtitle
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<PersonAddIcon />}
            label="New Users (7 days)"
            value={stats?.new_users_7d}
            color="success"
            onClick={() => navigate('/users?filter=new_7d')}
            subtitle
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<CreditCardIcon />}
            label="Active Subscriptions"
            value={stats?.active_subscriptions}
            color="info"
            onClick={() => navigate('/users?subscription_status=active')}
            subtitle
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<ShieldIcon />}
            label="Clients Covered"
            value={stats?.total_clients_covered}
            color="secondary"
            onClick={() => navigate('/users?account_type=client,client_solo')}
            subtitle
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MessageIcon />}
            label="Events (24h)"
            value={stats?.events_24h}
            color="warning"
            onClick={() => navigate('/users?filter=recent_activity')}
            subtitle
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<HourglassIcon />}
            label="Pending Verifications"
            value={stats?.pending_verifications}
            color="error"
            onClick={() => navigate('/users?filter=pending_verifications')}
            subtitle
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/users')}
            >
              All Users →
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/users?is_super_admin=true')}
            >
              Super Admins →
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/users?subscription_status=past_due')}
            >
              Past Due →
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/users?email_verified=false')}
            >
              Unverified Emails →
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/audit-log')}
            >
              Audit Log →
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Dashboard;
